<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="ds-page">
    <div class="ds-container ds-container--wide">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">بانرات الموقع</h1>
          <p class="ds-page-header__subtitle">إدارة الإعلانات والبانرات التي تظهر للطلاب على الموقع</p>
        </div>
        <button class="ds-btn ds-btn--primary" @click="openCreate">
          <i class="pi pi-plus"></i> بانر جديد
        </button>
      </div>

      <Loading v-if="store.loading" />

      <template v-else>
        <div v-if="!store.banners.length" class="empty-state empty-state--card">
          <div class="empty-state__icon"><i class="pi pi-images"></i></div>
          <h3 class="empty-state__title">لا توجد بانرات</h3>
          <p class="empty-state__desc">أضف أول بانر ليظهر على الصفحة الرئيسية للطلاب.</p>
        </div>

        <div v-else class="ds-table-wrap table-responsive">
          <table class="table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3" style="width:70px">الترتيب</th>
                <th>البانر</th>
                <th class="text-center">النوع</th>
                <th class="text-center">الحالة</th>
                <th>الفترة</th>
                <th>السنة الدراسية</th>
                <th class="text-center pe-3">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(b, i) in store.banners" :key="b.id">
                <td class="ps-3">
                  <div class="reorder-group">
                    <button class="icon-action-btn icon-action-btn--neutral" :disabled="i === 0" v-tooltip.top="'لأعلى'" @click="move(i, -1)"><i class="pi pi-arrow-up"></i></button>
                    <button class="icon-action-btn icon-action-btn--neutral" :disabled="i === store.banners.length - 1" v-tooltip.top="'لأسفل'" @click="move(i, 1)"><i class="pi pi-arrow-down"></i></button>
                  </div>
                </td>
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <img v-if="b.image_link" :src="b.image_link" class="banner-thumb" alt="" />
                    <div v-else class="banner-thumb banner-thumb--empty"><i class="pi pi-align-left"></i></div>
                    <div class="min-w-0">
                      <div class="fw-bold text-truncate">{{ b.title || '— بدون عنوان —' }}</div>
                      <small class="text-muted text-truncate d-block">{{ b.description }}</small>
                    </div>
                  </div>
                </td>
                <td class="text-center"><span class="status-badge status-badge--info">{{ typeLabel(b.type) }}</span></td>
                <td class="text-center">
                  <button class="status-badge" :class="b.is_active ? 'status-badge--accepted' : 'status-badge--muted'" @click="toggle(b)">
                    {{ b.is_active ? 'مفعّل' : 'موقوف' }}
                  </button>
                </td>
                <td><small class="text-muted">{{ periodLabel(b) }}</small></td>
                <td>
                  <span class="status-badge" :class="b.academic_year_id ? 'status-badge--info' : 'status-badge--muted'">
                    {{ b.academic_year?.name || 'عام (الجميع)' }}
                  </span>
                </td>
                <td class="text-center pe-3">
                  <div class="icon-action-group justify-content-center">
                    <button class="icon-action-btn icon-action-btn--neutral" v-tooltip.top="'معاينة'" @click="preview(b)"><i class="pi pi-eye"></i></button>
                    <button class="icon-action-btn icon-action-btn--primary" v-tooltip.top="'تعديل'" @click="openEdit(b)"><i class="pi pi-pencil"></i></button>
                    <button class="icon-action-btn icon-action-btn--danger" v-tooltip.top="'حذف'" @click="remove(b)"><i class="pi pi-trash"></i></button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>

  <!-- Create / Edit dialog -->
  <Dialog v-model:visible="formOpen" modal :style="{ width: '46rem' }" :breakpoints="{ '640px': '95vw' }" :header="editing ? 'تعديل البانر' : 'بانر جديد'">
    <div class="ds-field-grid">
      <div class="form-field">
        <label class="field-label">نوع البانر</label>
        <select class="form-select" v-model="form.type">
          <option value="image_text">صورة ونص</option>
          <option value="image">صورة فقط</option>
          <option value="text">نص فقط</option>
        </select>
      </div>
      <div class="form-field">
        <label class="field-label">الحالة</label>
        <select class="form-select" v-model="form.is_active">
          <option :value="true">مفعّل</option>
          <option :value="false">موقوف</option>
        </select>
      </div>
    </div>

    <div v-if="form.type !== 'image'" class="form-field mt-2">
      <label class="field-label">العنوان</label>
      <input type="text" class="form-control" v-model="form.title" placeholder="عنوان البانر" />
    </div>
    <div v-if="form.type !== 'image'" class="form-field mt-2">
      <label class="field-label">الوصف <span class="optional">اختياري</span></label>
      <textarea class="form-control" rows="2" v-model="form.description" placeholder="نص الإعلان"></textarea>
    </div>

    <div v-if="form.type !== 'text'" class="form-field mt-2">
      <label class="field-label">الصورة</label>
      <input type="file" class="form-control" accept="image/*" @change="onFile" />
      <small class="text-muted">أقصى حجم 5MB.</small>
    </div>

    <div class="ds-field-grid mt-2">
      <div class="form-field">
        <label class="field-label">نص الزر <span class="optional">اختياري</span></label>
        <input type="text" class="form-control" v-model="form.button_text" placeholder="مثال: اعرف المزيد" />
      </div>
      <div class="form-field">
        <label class="field-label">رابط الزر <span class="optional">اختياري</span></label>
        <input type="text" class="form-control" v-model="form.button_url" dir="ltr" placeholder="https://..." />
      </div>
    </div>

    <div class="ds-field-grid mt-2">
      <div class="form-field">
        <label class="field-label">تاريخ البدء <span class="optional">اختياري</span></label>
        <input type="datetime-local" class="form-control" v-model="form.starts_at" />
      </div>
      <div class="form-field">
        <label class="field-label">تاريخ الانتهاء <span class="optional">اختياري</span></label>
        <input type="datetime-local" class="form-control" v-model="form.ends_at" />
      </div>
    </div>

    <div class="form-field mt-2">
      <label class="field-label">السنة الدراسية <span class="optional">اختياري</span></label>
      <select class="form-select" v-model="form.academic_year_id">
        <option :value="null">كل السنوات الدراسية (بانر عام)</option>
        <option v-for="y in gradeStore.activeGrades" :key="y.id" :value="y.id">{{ y.name }}</option>
      </select>
      <small class="text-muted">اتركه بدون تحديد ليظهر البانر لجميع الطلاب.</small>
    </div>

    <!-- Live preview -->
    <div class="preview-block mt-3">
      <div class="preview-block__label">معاينة</div>
      <BannerCardPreview :banner="previewModel" />
    </div>

    <template #footer>
      <button class="btn-cancel" @click="formOpen = false">إلغاء</button>
      <button class="ds-btn ds-btn--primary" :disabled="store.saving" @click="save">
        <i class="pi pi-check"></i> {{ store.saving ? 'جارٍ الحفظ...' : 'حفظ' }}
      </button>
    </template>
  </Dialog>

  <!-- Standalone preview dialog -->
  <Dialog v-model:visible="previewOpen" modal :style="{ width: '40rem' }" :breakpoints="{ '640px': '95vw' }" header="معاينة البانر">
    <BannerCardPreview v-if="previewBanner" :banner="previewBanner" />
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import BannerCardPreview from './BannerCardPreview.vue';
import { useBannerStore } from '@/stores/useBannerStore.js';
import { useGradeStore } from '@/stores/useGradeStore.js';

const TYPE_LABELS = { image: 'صورة', text: 'نص', image_text: 'صورة ونص' };

function blankForm() {
  return {
    id: null, type: 'image_text', title: '', description: '',
    button_text: '', button_url: '', is_active: true,
    starts_at: '', ends_at: '', imageFile: null, imagePreview: null, existingImage: null,
    academic_year_id: null,
  };
}

export default {
  name: 'BannersIndex',
  components: { Dialog, Breadcrumb, Loading, BannerCardPreview },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'بانرات الموقع', path: '/banners' }],
      formOpen: false,
      editing: false,
      form: blankForm(),
      previewOpen: false,
      previewBanner: null,
    };
  },
  computed: {
    store() { return useBannerStore(); },
    gradeStore() { return useGradeStore(); },
    previewModel() {
      return {
        type: this.form.type,
        title: this.form.title,
        description: this.form.description,
        button_text: this.form.button_text,
        button_url: this.form.button_url,
        image_link: this.form.imagePreview || this.form.existingImage,
      };
    },
  },
  methods: {
    typeLabel(t) { return TYPE_LABELS[t] || t; },
    periodLabel(b) {
      const f = (d) => d ? new Date(d).toLocaleDateString('ar-EG') : null;
      const s = f(b.starts_at), e = f(b.ends_at);
      if (s && e) return `${s} — ${e}`;
      if (s) return `من ${s}`;
      if (e) return `حتى ${e}`;
      return 'دائم';
    },
    toLocal(d) { return d ? new Date(d).toISOString().slice(0, 16) : ''; },
    openCreate() {
      this.editing = false;
      this.form = blankForm();
      this.formOpen = true;
    },
    openEdit(b) {
      this.editing = true;
      this.form = {
        id: b.id, type: b.type, title: b.title || '', description: b.description || '',
        button_text: b.button_text || '', button_url: b.button_url || '',
        is_active: !!b.is_active, starts_at: this.toLocal(b.starts_at), ends_at: this.toLocal(b.ends_at),
        imageFile: null, imagePreview: null, existingImage: b.image_link || null,
        academic_year_id: b.academic_year_id ?? null,
      };
      this.formOpen = true;
    },
    onFile(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.form.imageFile = file;
      this.form.imagePreview = URL.createObjectURL(file);
    },
    buildPayload() {
      const fd = new FormData();
      fd.append('type', this.form.type);
      if (this.form.title) fd.append('title', this.form.title);
      if (this.form.description) fd.append('description', this.form.description);
      if (this.form.button_text) fd.append('button_text', this.form.button_text);
      if (this.form.button_url) fd.append('button_url', this.form.button_url);
      fd.append('is_active', this.form.is_active ? 1 : 0);
      if (this.form.starts_at) fd.append('starts_at', this.form.starts_at);
      if (this.form.ends_at) fd.append('ends_at', this.form.ends_at);
      if (this.form.imageFile) fd.append('image', this.form.imageFile);
      // Always sent (even empty) so switching back to "بدون سنة دراسية" on an
      // edit actually clears a previously-set value instead of being silently
      // dropped (the backend only touches fields present in the payload).
      fd.append('academic_year_id', this.form.academic_year_id ?? '');
      return fd;
    },
    async save() {
      try {
        const payload = this.buildPayload();
        if (this.editing) await this.store.updateBanner(this.form.id, payload);
        else await this.store.createBanner(payload);
        this.formOpen = false;
        await this.store.fetchBanners();
        Swal.fire({ icon: 'success', title: 'تم الحفظ', timer: 1400, showConfirmButton: false });
      } catch (e) {
        const msg = e?.response?.data?.message || Object.values(e?.response?.data?.errors || {})[0]?.[0] || 'حدث خطأ';
        Swal.fire({ icon: 'error', title: msg, timer: 2500, showConfirmButton: false });
      }
    },
    async toggle(b) {
      try { await this.store.toggleActive(b.id); }
      catch { Swal.fire({ icon: 'error', title: 'تعذّر تغيير الحالة', timer: 2000, showConfirmButton: false }); }
    },
    async remove(b) {
      const { isConfirmed } = await Swal.fire({
        title: 'حذف البانر؟', text: 'لا يمكن التراجع عن هذا الإجراء.', icon: 'warning',
        showCancelButton: true, confirmButtonText: 'حذف', cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!isConfirmed) return;
      try {
        await this.store.deleteBanner(b.id);
        Swal.fire({ icon: 'success', title: 'تم الحذف', timer: 1400, showConfirmButton: false });
      } catch { Swal.fire({ icon: 'error', title: 'تعذّر الحذف', timer: 2000, showConfirmButton: false }); }
    },
    async move(index, dir) {
      const arr = [...this.store.banners];
      const target = index + dir;
      if (target < 0 || target >= arr.length) return;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      this.store.banners = arr;
      try { await this.store.reorder(arr.map(b => b.id)); }
      catch { await this.store.fetchBanners(); }
    },
    preview(b) {
      this.previewBanner = { ...b };
      this.previewOpen = true;
    },
  },
  async mounted() {
    this.store.fetchBanners();
    if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
  },
};
</script>

<style scoped>
.banner-thumb { width: 64px; height: 40px; object-fit: cover; border-radius: 8px; flex-shrink: 0; }
.banner-thumb--empty { display: flex; align-items: center; justify-content: center; background: var(--surface-100, #f1f5f9); color: var(--text-secondary); }
.min-w-0 { min-width: 0; }
.reorder-group { display: flex; gap: 0.25rem; }
.status-badge--muted { background: rgba(148,163,184,.15); color: #64748b; }
.status-badge--info { background: rgba(91,95,239,.12); color: #5B5FEF; }
.preview-block { border: 1px dashed var(--surface-border, #e5e7eb); border-radius: 12px; padding: 1rem; background: var(--surface-50, #f8fafc); }
.preview-block__label { font-size: .8rem; font-weight: 700; color: var(--text-secondary); margin-bottom: .6rem; }
</style>
